import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DEFAULT_SEO, SEO_CONFIG, SeoMeta } from './seo-config';

const JSONLD_ID = 'seo-page-jsonld';

/**
 * Centralised SEO meta manager for public pages.
 * Listens to router navigation and applies title / description / keywords
 * (plus Open Graph & Twitter tags) from SEO_CONFIG, with a sensible default.
 *
 * Dynamic pages (e.g. product detail) can call `update()` from their component
 * to override the tags with content-specific values.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly siteName = 'LegalStack';
  private readonly origin = 'https://legalstack.in';

  constructor(private router: Router, private title: Title, private meta: Meta) {}

  /** Call once from AppComponent to start reacting to route changes. */
  init(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const path = (e.urlAfterRedirects || e.url).split('?')[0].split('#')[0].replace(/^\/+/, '');
        this.apply(SEO_CONFIG[path] ?? DEFAULT_SEO, e.urlAfterRedirects || e.url);
      });
  }

  /** Override tags for a specific (often dynamic) page. */
  update(meta: Partial<SeoMeta>, url?: string): void {
    this.apply({ ...DEFAULT_SEO, ...meta }, url ?? this.router.url);
  }

  private apply(seo: SeoMeta, url: string): void {
    const canonical = this.origin + (url.startsWith('/') ? url : '/' + url).split('?')[0].split('#')[0];

    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ name: 'keywords', content: seo.keywords });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
    this.meta.updateTag({ property: 'og:url', content: canonical });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });

    this.setCanonical(canonical);
    this.setJsonLd(seo, canonical);
  }

  /** Inject per-page Service + FAQPage structured data (replaced each navigation). */
  private setJsonLd(seo: SeoMeta, canonical: string): void {
    const graph: any[] = [];

    if (seo.service) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: seo.service,
        serviceType: seo.service,
        description: seo.description,
        url: canonical,
        areaServed: 'IN',
        provider: { '@type': 'Organization', name: this.siteName, url: this.origin + '/' },
      });
    }

    if (seo.faqs && seo.faqs.length) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: seo.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    const existing = document.getElementById(JSONLD_ID);
    if (!graph.length) {
      if (existing) existing.remove();
      return;
    }

    const payload = graph.length === 1 ? graph[0] : graph;
    let script = existing as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSONLD_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(payload);
  }

  private setCanonical(href: string): void {
    let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
